import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { PreferencesService } from './services/preferences.service';
import { TranslationService } from './i18n/translation.service';
import { clearStaleInteractionLock } from './auth/interaction-lock';
import { ExternalIdSignInService } from './auth/external-id-sign-in.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  constructor(
    private router: Router,
    private msalService: MsalService,
    private preferences: PreferencesService,
    private translation: TranslationService,
    private externalId: ExternalIdSignInService
  ) {}

  /**
   * LANGUAGE FOLLOWS THE PREFERENCE CASCADE, which resolves global -> company ->
   * user on the server. So this subscribes to the RESOLVED value rather than
   * reading a tier: the client does not re-derive a precedence that already has
   * one implementation.
   *
   * English is preloaded regardless, because it is the fallback for every other
   * language and a miss is discovered mid-render, where a fetch cannot be
   * awaited. distinctUntilChanged keeps a preferences refresh that did not
   * change the language from re-fetching a dictionary.
   */
  private followPreferredLanguage(): void {
    this.translation.preloadFallback().subscribe();
    this.preferences.effective$
      .pipe(
        map((prefs) => prefs.language),
        distinctUntilChanged(),
        switchMap((language) => this.translation.use(language))
      )
      .subscribe();
  }

  /**
   * START ROUTING ONLY AFTER MSAL HAS CONSUMED A REDIRECT RESPONSE.
   *
   * Angular's automatic initial navigation is disabled (see
   * app-routing.module.ts) because it raced MSAL and destroyed the auth
   * response. Measured on the dev server: the fragment was present at
   * document-start and gone 23ms later, taken by a pushState to /login that the
   * parent route's userAuthGuard triggered — it saw no account, because MSAL had
   * not processed the code yet. Angular builds a new UrlTree for that
   * navigation and the fragment does not survive it.
   *
   * WAITING ON inProgress$ DOES NOT WORK HERE, and that is worth stating because
   * it is the obvious thing to try: the subject starts at
   * InteractionStatus.None, so a "wait until None" gate is satisfied instantly
   * on a cold load — before MSAL has begun. handleRedirectPromise resolving is
   * the only signal that means "the response has been dealt with".
   *
   * The promise is awaited on BOTH paths. A rejection still has to start the
   * app: an auth failure that also froze the router would leave a blank page
   * with nothing to click.
   */
  ngOnInit(): void {
    this.followPreferredLanguage();
    // INITIALIZE FIRST. MSAL v4 refuses every other API until initialize() has
    // resolved, so calling handleRedirectPromise() straight away threw
    // `uninitialized_public_client_application` — the catch below swallowed it and
    // started routing IMMEDIATELY, which is precisely the race this gate exists to
    // prevent. The gate was a no-op; sign-in survived because userAuthGuard waits
    // on msalSettled independently. Found by driving the login page in a browser
    // and reading the console, not by any spec.
    this.msalService.instance
      .initialize()
      .then(() => this.msalService.instance.handleRedirectPromise())
      .then(async (result) => {
        // THEN the External ID (CIAM) instance, before the router moves. The workforce
        // instance above returns null and leaves the URL alone when the redirect was
        // CIAM's (it checks the interaction flag's client id), so this one consumes it.
        // It answers with the page the sign-in started from ("/invite"); land there
        // instead of the bare origin the authority returned to.
        const externalReturn = await this.externalId.handleRedirect().catch(() => null);
        if (externalReturn) {
          history.replaceState(history.state, '', externalReturn);
        }
        // Nothing is in flight at this point: both handlers have resolved.
        // A flag still set therefore belongs to an interaction that never came
        // back, and would throw interaction_in_progress on the next sign-in.
        clearStaleInteractionLock(!!result || !!externalReturn, globalThis.location?.hash ?? '');
        return result;
      })
      .catch((err) => {
        // Still non-fatal: a failure here must not leave the app unrouted and
        // blank. Routing proceeds and the guard decides what the user may see.
        console.warn('AppComponent: MSAL redirect handling failed', err);
      })
      .then(() => this.router.initialNavigation());
  }
}
